/**
 * Get display text for status (normalizes common status strings)
 */
export const getStatusDisplayText = (status: string | null | undefined): string => {
  if (!status) return 'N/A';

  const normalized = status.toLowerCase().trim();

  // Map specific statuses to display text
  const displayMap: Record<string, string> = {
    commandactioncreated: 'Not Started',
  };

  return displayMap[normalized] || status;
};

/**
 * Get status badge class with conditional coloring
 * - Completed: Green (success)
 * - CompletedWithErrors: Red (error)
 * - InProgress: Yellow (warning)
 * - Others: Amber (neutral/pending)
 */
export const getConditionalStatusBadgeClass = (status: string | null | undefined): string => {
  if (!status) return 'bg-amber-900/30 text-amber-400 rounded-md text-sm font-medium px-2 py-1';

  const normalized = status.toLowerCase().trim();

  if (normalized === 'completed') {
    return 'bg-green-900/30 text-green-400 rounded-md text-sm font-medium px-2 py-1';
  }

  if (normalized === 'completedwitherrors') {
    return 'bg-red-900/30 text-red-400 rounded-md text-sm font-medium px-2 py-1';
  }

  if (normalized.includes('progress') || normalized.includes('inprogress')) {
    return 'bg-yellow-900/30 text-yellow-400 rounded-md text-sm font-medium px-2 py-1';
  }

  // Return amber for all other statuses (e.g., Pending, Not Started, etc.)
  return 'bg-amber-900/30 text-amber-400 rounded-md text-sm font-medium px-2 py-1';
};

/**
 * Check if status is a failure/error state
 * Used for filtering failed items
 */
export const isFailedStatus = (status: string | null | undefined): boolean => {
  if (!status) return false;
  const normalized = status.toLowerCase().trim();
  return (
    normalized.includes('fail') || normalized.includes('error') || normalized.includes('timeout') || normalized === 'completedwitherrors'
  );
};

/**
 * Returns CSS classes for severity badges based on severity level
 * @param severity - The severity level (critical, high, medium, low)
 * @returns CSS classes for styling
 */
export const getSeverityColor = (severity: string): string => {
  if (!severity) return 'bg-gray-500/20 border-gray-500 text-gray-300';

  switch (severity.toLowerCase()) {
    case 'critical':
      return 'bg-red-700/20 border-red-700 text-red-300';
    case 'high':
      return 'bg-red-500/20 border-red-500 text-red-300';
    case 'medium':
      return 'bg-yellow-500/20 border-yellow-500 text-yellow-300';
    case 'low':
      return 'bg-green-500/20 border-green-500 text-green-300';
    default:
      return 'bg-gray-500/20 border-gray-500 text-gray-300';
  }
};
